import React, { FC, memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ReactSelect, { InputActionMeta } from 'react-select';

import usePrefix from 'utils/usePrefix';
import { AdminSubmissionFilters } from 'utils/api/resqlexpert.d';
import { FilterOption, filterOptionsApi } from 'utils/api/filterOptions';
import { usePaginatedOptions } from 'utils/usePaginatedOptions';

import Modal from 'components/Modal';
import { Label } from 'components/FormField/styles';
import Button from 'components/Button';
import PaginatedSelectMenu, { PaginatedMenuState } from 'components/PaginatedSelectMenu';

import {
  ExpertFilterActionsStyled,
  ExpertFilterDateWrapperStyled,
  ExpertFilterFormStyled,
  ExpertFilterModalContentStyled,
  ExpertFilterSelectWrapperStyled,
  InputFieldStyled,
} from './styles';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  filters: AdminSubmissionFilters;
  onApply: (filters: AdminSubmissionFilters) => void;
}

const getOptionValue = (option: FilterOption) => String(option.id);
const getOptionLabel = (option: FilterOption) => option.name;

const ExpertFilterModal: FC<Props> = ({ isOpen, onClose, filters, onApply }) => {
  const t = usePrefix('ResqlExpert');

  const [selectedSchool, setSelectedSchool] = useState<FilterOption | null>(null);
  const [selectedReporter, setSelectedReporter] = useState<FilterOption | null>(null);
  const [selectedExpert, setSelectedExpert] = useState<FilterOption | null>(null);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const schoolsPicker = usePaginatedOptions<FilterOption>({
    fetch: filterOptionsApi.listSchools,
    enabled: isOpen,
  });
  const reportersPicker = usePaginatedOptions<FilterOption>({
    fetch: filterOptionsApi.listReporters,
    enabled: isOpen,
  });
  const expertsPicker = usePaginatedOptions<FilterOption>({
    fetch: filterOptionsApi.listExperts,
    enabled: isOpen,
  });

  const filtersRef = useRef(filters);
  filtersRef.current = filters;
  const touchedRef = useRef({ school: false, reporter: false, expert: false });

  useEffect(() => {
    if (!isOpen) return;
    const f = filtersRef.current;
    touchedRef.current = { school: false, reporter: false, expert: false };

    setDateFrom(f.date_from ?? '');
    setDateTo(f.date_to ?? '');

    const hydrate = <T,>(
      id: number | undefined,
      fetcher: (id: number) => Promise<T>,
      setter: (v: T | null) => void,
      key: keyof typeof touchedRef.current,
    ) => {
      setter(null);
      if (!id) return;
      fetcher(id)
        .then((v) => {
          if (!touchedRef.current[key]) setter(v);
        })
        .catch(() => {});
    };

    hydrate(f.school_id, filterOptionsApi.getSchool, setSelectedSchool, 'school');
    hydrate(f.submitted_by, filterOptionsApi.getReporter, setSelectedReporter, 'reporter');
    hydrate(f.assigned_to, filterOptionsApi.getExpert, setSelectedExpert, 'expert');
  }, [isOpen]);

  const handleSchoolChange = (val: FilterOption | null) => {
    touchedRef.current.school = true;
    setSelectedSchool(val);
  };
  const handleReporterChange = (val: FilterOption | null) => {
    touchedRef.current.reporter = true;
    setSelectedReporter(val);
  };
  const handleExpertChange = (val: FilterOption | null) => {
    touchedRef.current.expert = true;
    setSelectedExpert(val);
  };

  const isDateRangeValid = !dateFrom || !dateTo || dateTo >= dateFrom;

  const handleDateFromChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setDateFrom(value);
    if (dateTo && value > dateTo) {
      setDateTo('');
    }
  };

  const handleDateToChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setDateTo(e.target.value);
  };

  const onApplyClick = useCallback(() => {
    if (!isDateRangeValid) return;
    onApply({
      school_id: selectedSchool?.id,
      submitted_by: selectedReporter?.id,
      assigned_to: selectedExpert?.id,
      date_from: dateFrom || undefined,
      date_to: dateTo || undefined,
    });
    onClose();
  }, [isDateRangeValid, selectedSchool, selectedReporter, selectedExpert, dateFrom, dateTo, onApply, onClose]);

  const onClearClick = useCallback(() => {
    setSelectedSchool(null);
    setSelectedReporter(null);
    setSelectedExpert(null);
    setDateFrom('');
    setDateTo('');
    onApply({});
    onClose();
  }, [onApply, onClose]);

  const labels = useMemo(
    () => ({ loading: t('loading'), error: t('picker_load_error'), retry: t('picker_retry') }),
    [t],
  );

  const buildPaginatedState = (picker: ReturnType<typeof usePaginatedOptions<FilterOption>>): PaginatedMenuState => ({
    loading: picker.loading,
    error: picker.error,
    hasMore: picker.hasMore,
    onRetry: picker.retry,
    labels,
  });

  const handleInputChange = (picker: ReturnType<typeof usePaginatedOptions<FilterOption>>) =>
    (value: string, action: InputActionMeta) => {
      if (action.action === 'input-change') picker.setSearch(value);
    };

  return (
    <Modal isOpen={isOpen} onClose={onClose} centered>
      <ExpertFilterModalContentStyled>
        <h3>{t('cta_filter')}</h3>
        <ExpertFilterFormStyled>
          <ExpertFilterSelectWrapperStyled>
            <Label htmlFor="select_school">{t('select_school')}</Label>
            <ReactSelect
              inputId="select_school"
              options={schoolsPicker.options}
              value={selectedSchool}
              getOptionValue={getOptionValue}
              getOptionLabel={getOptionLabel}
              onChange={(val) => handleSchoolChange(val as FilterOption | null)}
              onInputChange={handleInputChange(schoolsPicker)}
              onMenuScrollToBottom={schoolsPicker.loadMore}
              filterOption={() => true}
              isLoading={schoolsPicker.loading && schoolsPicker.options.length === 0}
              isClearable
              placeholder={t('select_school_placeholder')}
              classNamePrefix="picker"
              components={{ MenuList: PaginatedSelectMenu }}
              paginatedState={buildPaginatedState(schoolsPicker)}
            />
          </ExpertFilterSelectWrapperStyled>

          <ExpertFilterSelectWrapperStyled>
            <Label htmlFor="selectReporter">{t('select_reporter')}</Label>
            <ReactSelect
              inputId="selectReporter"
              options={reportersPicker.options}
              value={selectedReporter}
              getOptionValue={getOptionValue}
              getOptionLabel={getOptionLabel}
              onChange={(val) => handleReporterChange(val as FilterOption | null)}
              onInputChange={handleInputChange(reportersPicker)}
              onMenuScrollToBottom={reportersPicker.loadMore}
              filterOption={() => true}
              isLoading={reportersPicker.loading && reportersPicker.options.length === 0}
              isClearable
              placeholder={t('select_reporter_placeholder')}
              classNamePrefix="picker"
              components={{ MenuList: PaginatedSelectMenu }}
              paginatedState={buildPaginatedState(reportersPicker)}
            />
          </ExpertFilterSelectWrapperStyled>

          <ExpertFilterSelectWrapperStyled>
            <Label htmlFor="select_expert">{t('select_expert')}</Label>
            <ReactSelect
              inputId="select_expert"
              options={expertsPicker.options}
              value={selectedExpert}
              getOptionValue={getOptionValue}
              getOptionLabel={getOptionLabel}
              onChange={(val) => handleExpertChange(val as FilterOption | null)}
              onInputChange={handleInputChange(expertsPicker)}
              onMenuScrollToBottom={expertsPicker.loadMore}
              filterOption={() => true}
              isLoading={expertsPicker.loading && expertsPicker.options.length === 0}
              isClearable
              placeholder={t('select_expert_placeholder')}
              classNamePrefix="picker"
              components={{ MenuList: PaginatedSelectMenu }}
              paginatedState={buildPaginatedState(expertsPicker)}
            />
          </ExpertFilterSelectWrapperStyled>

          <ExpertFilterDateWrapperStyled>
            <ExpertFilterSelectWrapperStyled>
              <Label htmlFor="dateFrom">{t('date_from')}</Label>
              <InputFieldStyled
                id="dateFrom"
                type="date"
                value={dateFrom}
                onChange={handleDateFromChange}
              />
            </ExpertFilterSelectWrapperStyled>

            <ExpertFilterSelectWrapperStyled>
              <Label htmlFor="dateTo">{t('date_to')}</Label>
              <InputFieldStyled
                id="dateTo"
                type="date"
                value={dateTo}
                min={dateFrom || undefined}
                onChange={handleDateToChange}
              />
            </ExpertFilterSelectWrapperStyled>
          </ExpertFilterDateWrapperStyled>

          <ExpertFilterActionsStyled>
            <Button label={t('cta_cancel')} color="secondary" onClick={onClose} />
            <Button label={t('cta_clear_filter')} color="secondary" onClick={onClearClick} />
            <Button
              label={t('cta_filter')}
              color="primary"
              onClick={onApplyClick}
              disabled={!isDateRangeValid}
            />
          </ExpertFilterActionsStyled>
        </ExpertFilterFormStyled>
      </ExpertFilterModalContentStyled>
    </Modal>
  );
};

export default memo(ExpertFilterModal);
